import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { take, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AUTH_STATE } from '../tokens/auth-state.token';
import { GET_USER_STATUS } from '../tokens/user-status.token';
import { computeEffectiveStatus } from '../models/user-status.model';
import { foreignRoleRedirect } from './role-areas';

/**
 * Guards /accommodation (view): requires authenticated user whose effective status
 * is approval_pending or approved. accommodation_pending and earlier redirect to /home.
 * Managers are redirected to /manager.
 */
export const accommodationViewGuard: CanActivateFn = () => {
  const authState$ = inject(AUTH_STATE);
  const getUserStatus = inject(GET_USER_STATUS);
  const router = inject(Router);

  return authState$.pipe(
    take(1),
    switchMap((user) => {
      if (!user) return [router.createUrlTree(['/'])];
      return getUserStatus(user.uid).pipe(
        map((status) => {
          if (!status) return router.createUrlTree(['/home']);
          const away = foreignRoleRedirect(router, status.role, ['socio']);
          if (away) return away;
          const effective = computeEffectiveStatus(
            status.registrationStatus,
            user.emailVerified,
          );
          if (
            effective === 'email_unverified' ||
            effective === 'profile_pending' ||
            effective === 'accommodation_pending'
          ) {
            return router.createUrlTree(['/home']);
          }
          return true;
        }),
      );
    }),
  );
};

/**
 * Guards /accommodation/edit: requires authenticated user whose effective status
 * is accommodation_pending, approval_pending, or approved.
 * Managers are redirected to /manager.
 * email_unverified and profile_pending redirect to /home.
 */
export const accommodationEditGuard: CanActivateFn = () => {
  const authState$ = inject(AUTH_STATE);
  const getUserStatus = inject(GET_USER_STATUS);
  const router = inject(Router);

  return authState$.pipe(
    take(1),
    switchMap((user) => {
      if (!user) return [router.createUrlTree(['/'])];
      return getUserStatus(user.uid).pipe(
        map((status) => {
          if (!status) return router.createUrlTree(['/home']);
          const away = foreignRoleRedirect(router, status.role, ['socio']);
          if (away) return away;
          const effective = computeEffectiveStatus(
            status.registrationStatus,
            user.emailVerified,
          );
          if (effective === 'email_unverified' || effective === 'profile_pending') {
            return router.createUrlTree(['/home']);
          }
          return true;
        }),
      );
    }),
  );
};

/**
 * Guards /accommodation/availability: requires an authenticated socio who has at
 * least reached accommodation_pending, so the socio can open days as part of the
 * registration flow (before approval).
 * - Anonymous users → /
 * - Managers → /manager
 * - email_unverified / profile_pending → /home
 * - accommodation_pending, approval_pending, approved → allow
 */
export const availabilityGuard: CanActivateFn = () => {
  const authState$ = inject(AUTH_STATE);
  const getUserStatus = inject(GET_USER_STATUS);
  const router = inject(Router);

  return authState$.pipe(
    take(1),
    switchMap((user) => {
      if (!user) return [router.createUrlTree(['/'])];
      return getUserStatus(user.uid).pipe(
        map((status) => {
          if (!status) return router.createUrlTree(['/home']);
          const away = foreignRoleRedirect(router, status.role, ['socio']);
          if (away) return away;
          const effective = computeEffectiveStatus(
            status.registrationStatus,
            user.emailVerified,
          );
          if (effective === 'email_unverified' || effective === 'profile_pending') {
            return router.createUrlTree(['/home']);
          }
          return true;
        }),
      );
    }),
  );
};

/**
 * Guards /accommodation/photos: same rules as availabilityGuard — the socio
 * uploads photos as part of registration, before approval.
 */
export const photosGuard: CanActivateFn = () => {
  const authState$ = inject(AUTH_STATE);
  const getUserStatus = inject(GET_USER_STATUS);
  const router = inject(Router);

  return authState$.pipe(
    take(1),
    switchMap((user) => {
      if (!user) return [router.createUrlTree(['/'])];
      return getUserStatus(user.uid).pipe(
        map((status) => {
          if (!status) return router.createUrlTree(['/home']);
          const away = foreignRoleRedirect(router, status.role, ['socio']);
          if (away) return away;
          const effective = computeEffectiveStatus(
            status.registrationStatus,
            user.emailVerified,
          );
          if (effective === 'email_unverified' || effective === 'profile_pending') {
            return router.createUrlTree(['/home']);
          }
          return true;
        }),
      );
    }),
  );
};
